import ScoreGauge from './ScoreGauge'
import Linked from 'shared-components/service/Linked'
import styles from './FeaturedScores.module.css'
import DistributionBars from './DistributionBars'
import { useCatalogEvent } from 'lib/analytics/catalog'
import { scrollToSection } from './OnThisPageNav'
import {
  FEATURED_SCORE_DEFAULTS,
  HOW_WE_TEST_URL,
  MIN_MEASURED_PEERS,
  type FeaturedScoreDefault,
} from 'lib/dealhunt/constants'
import type { SoundGuysReviewModel } from 'lib/dealhunt/review'
import type { RatingDistribution, RatingDistributionsResponse } from 'lib/dealhunt/types'

interface RankClass {
  name: string
}

interface FeaturedScoresProps {
  review: SoundGuysReviewModel
  /** Class score distributions (DealHunt /api/rating-distributions); null degrades to rings only. */
  ratingDistributions: RatingDistributionsResponse | null
  /** Fallback peer set for the header while rating distributions are unavailable. */
  rankClass: RankClass | null
  /** `#sec-labs` when the page renders lab curves; null keeps the methodology link. */
  labHref?: string | null
  /** "Frequency response · Isolation · Noise cancelling" — the tabs the reader will find. */
  labSummary?: string | null
}

interface ScoreRow {
  def: FeaturedScoreDefault
  value: number
  dist: RatingDistribution | null
}

/**
 * Lowercase a display name for use mid-sentence while preserving all-caps tokens —
 * "Noise Isolation / ANC" → "noise isolation / ANC", not "…/ anc". A run of ≥2
 * uppercase letters/digits reads as an acronym (ANC, IP, MDAQS) and stays as-is.
 */
function lowerPhrase(name: string): string {
  return name
    .split(/(\s+)/)
    .map(tok => (/^[A-Z0-9]{2,}$/.test(tok) ? tok : tok.toLowerCase()))
    .join('')
}

/**
 * Copy for a dimension DealHunt features that we have no hand-written entry for.
 * The schema's display name carries the wording; the scale comes from the response's
 * own fixed bounds, so a /5 lab score and a /10 editorial score both read correctly.
 * Editors pick featured scores per category in SG Studio — this is what keeps a new
 * pick renderable without a SoundGuys deploy.
 */
function derivedDefault(dist: RatingDistribution): FeaturedScoreDefault {
  const name = dist.label
  return {
    heading: name,
    label: name,
    sub: '',
    keys: [dist.ratingType],
    phrase: lowerPhrase(name),
    copyStyle: 'tier',
    scaleMin: dist.stats.min,
    scaleMax: dist.stats.max,
  }
}

/** Our hand-written copy for a key, when we have it. */
function defaultFor(ratingType: string): FeaturedScoreDefault | null {
  return FEATURED_SCORE_DEFAULTS.find(d => d.keys.includes(ratingType)) ?? null
}

/**
 * The product's score for a featured slot, from the review model. MDAQS keys live in
 * `review.mdaqs` (on the /5 source scale); everything else in `review.subScores` (/10).
 */
function resolveValue(def: FeaturedScoreDefault, review: SoundGuysReviewModel): number | null {
  for (const key of def.keys) {
    const mdaqs = review.mdaqs.find(m => m.key === key)
    if (mdaqs) {
      return mdaqs.score
    }
    const sub = review.subScores.find(s => s.key === key)
    if (sub) {
      return sub.score
    }
  }
  return null
}

/**
 * Percentile tier phrase, or null for the genuine mid-range (34–49%) where the raw
 * "beats N%" is the honest framing. A symmetric bottom ladder replaces the faint-praise
 * "beats 14%" line for low performers with a plain standing ("the bottom 10%").
 * `percentile` is the share strictly below the score, so ≤25 → bottom quarter is exact.
 */
function tierOf(percentile: number): string | null {
  if (percentile >= 90) {
    return 'the top 10%'
  }
  if (percentile >= 75) {
    return 'the top quarter'
  }
  if (percentile >= 67) {
    return 'the top third'
  }
  if (percentile >= 50) {
    return 'the top half'
  }
  if (percentile <= 10) {
    return 'the bottom 10%'
  }
  if (percentile <= 25) {
    return 'the bottom quarter'
  }
  if (percentile <= 33) {
    return 'the bottom third'
  }
  return null
}

/**
 * Percentile sentence parts: prefix text, emphasized (linked) phrase, suffix text.
 * Formulaic only — derived from the percentile, never editorial. The emphasis links
 * to the methodology page.
 */
function sentenceOf(
  row: ScoreRow,
  percentile: number,
  peersName: string
): { pre: string; em: string; post: string } {
  const { def, value } = row
  const tier = def.copyStyle === 'tier' ? tierOf(percentile) : null
  if (tier) {
    return {
      pre: `A ${value} puts its ${def.phrase} in `,
      em: tier,
      post: ` of ${peersName} we've measured.`,
    }
  }
  return {
    pre: `At ${value}, its ${def.phrase} beats `,
    em: `${percentile}%`,
    post: ` of ${peersName} we've measured.`,
  }
}

/**
 * One score row: ring gauge + its class distribution and percentile copy.
 */
const ScoreRowView: React.VFC<{
  row: ScoreRow
  peersName: string
  /** Where "we've measured" points: the page's own lab section when it has one. */
  labHref: string | null
  onLabClick: (e: React.MouseEvent<HTMLAnchorElement>, location: string) => void
}> = ({ row, peersName, labHref, onLabClick }) => {
  const { def, value, dist } = row
  // The chart needs enough measured peers to read as signal; the pill pins to the
  // endpoint's merged value so ring/chart provenance can't silently diverge.
  const chart = dist && dist.value !== null && dist.known >= MIN_MEASURED_PEERS ? dist : null
  // A thin score is ranked against a WIDER class than the card header names (on-ear
  // MDAQS vs all headphones and earbuds) — the sentence must say which.
  const rowPeersName = chart?.class ? chart.class.name.toLowerCase() : peersName
  const sentence =
    chart && chart.percentile !== null ? sentenceOf(row, chart.percentile, rowPeersName) : null
  // Name the class when it isn't the one in the header, or "of the 1202" reads as a
  // count of the header's 92 on-ear headphones.
  let coverage: string | null = null
  if (chart) {
    coverage =
      rowPeersName === peersName
        ? `Measured on ${chart.known} of the ${chart.classSize} we've cataloged.`
        : `Measured on ${chart.known} of the ${chart.classSize} ${rowPeersName} we've cataloged.`
  }

  return (
    <div className={styles.row}>
      <ScoreGauge value={value} max={def.scaleMax} label={def.label} sub={def.sub || undefined} />
      <div className={styles.detail}>
        {chart && (
          <DistributionBars
            compact
            distribution={chart.distribution}
            productBucket={chart.productBucket}
            markerText={`${chart.value}`}
            axisMin={`${chart.stats.min.toFixed(1)}`}
            axisMax={`${chart.stats.max.toFixed(1)}`}
          />
        )}
        {sentence && chart && (
          <>
            <p className={styles.sentence}>
              {sentence.pre}
              {labHref ? (
                // "…we've measured" → the measurements one scroll down, not the
                // methodology page: the readers who tap this are the ones who want curves.
                <Linked
                  className={styles.emphasis}
                  href={labHref}
                  onClick={e => onLabClick(e, 'hero_scores')}
                >
                  {sentence.em}
                </Linked>
              ) : (
                <Linked
                  className={styles.emphasis}
                  href={HOW_WE_TEST_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {sentence.em}
                </Linked>
              )}
              {sentence.post}
            </p>
            {coverage && <p className={styles.coverage}>{coverage}</p>}
          </>
        )}
        {!chart && (
          <p className={styles.sentence}>
            {`${def.sub || def.label} — measured in the SoundGuys lab.`}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * "Sound & noise cancelling" hero card: the category's featured review scores as
 * ring gauges, each with a class distribution + percentile sentence when DealHunt's
 * rating-distributions endpoint resolved (rings-only otherwise — optional enrichment).
 * Scores come from the PDP payload itself, so the card never waits on the endpoint.
 */
const FeaturedScores: React.VFC<FeaturedScoresProps> = ({
  review,
  ratingDistributions,
  rankClass,
  labHref = null,
  labSummary = null,
}) => {
  const { track } = useCatalogEvent()
  const onLabClick = (e: React.MouseEvent<HTMLAnchorElement>, location: string): void => {
    track('product_page_section_nav_click', { section: 'labs', location })
    // Same easing as the sticky nav tabs; fall back to the hash jump if the section
    // isn't in the DOM (it always is when labHref is set, but the guard is free).
    if (labHref && labHref.startsWith('#') && scrollToSection(labHref.slice(1))) {
      e.preventDefault()
    }
  }
  const distributions = ratingDistributions?.distributions ?? []
  const rows: ScoreRow[] = []
  if (distributions.length > 0) {
    // DealHunt decides WHICH scores are featured (per category, editable in SG
    // Studio) and in what order; we only own the wording. A key we have copy for
    // keeps it, anything else is derived from the schema's display name.
    for (const dist of [...distributions].sort((a, b) => a.displayOrder - b.displayOrder)) {
      const def = defaultFor(dist.ratingType) ?? derivedDefault(dist)
      // Prefer the endpoint's merged value: same provenance as the PDP's rating
      // pills, and the only source for a key our review model doesn't map.
      const value = dist.value ?? resolveValue(def, review)
      if (value === null) {
        continue
      }
      rows.push({ def, value, dist })
    }
  } else {
    // No response (endpoint down, or the category features nothing): fall back to
    // our own defaults so the rings still render from the review model.
    for (const def of FEATURED_SCORE_DEFAULTS) {
      const value = resolveValue(def, review)
      if (value === null) {
        continue
      }
      rows.push({ def, value, dist: null })
    }
  }
  if (!rows.length) {
    return null
  }

  // "Sound & noise cancelling" — the lead reads as a title, the rest as prose.
  const heading = rows
    .map((r, i) => (i === 0 ? r.def.heading : r.def.heading.toLowerCase()))
    .join(' & ')
  // The shared peer name every row's copy reads ("…of over-ear headphones we've
  // measured"). When every charted score was widened to the SAME class we use it;
  // a genuine mix falls back to the response/rank class so no single row's class
  // masquerades as the set's.
  // `?? null` is load-bearing during a staged deploy: a DealHunt build older than
  // the per-score widening serves rows with no `class` at all, and an undefined
  // slipping through this filter would crash the card on `.nodeId`.
  const chartedClasses = rows
    .map(r => (r.dist && r.dist.known >= MIN_MEASURED_PEERS ? r.dist.class ?? null : null))
    .filter((c): c is NonNullable<typeof c> => c !== null && c !== undefined)
  const unifiedClass =
    chartedClasses.length > 0 && chartedClasses.every(c => c.nodeId === chartedClasses[0].nodeId)
      ? chartedClasses[0]
      : null
  const peers = unifiedClass ?? ratingDistributions?.class ?? rankClass
  const peersName = peers ? peers.name.toLowerCase() : 'peers'

  return (
    <section className={styles.panel} aria-label={heading}>
      <div className={styles.head}>
        <span className={styles.eyebrow}>{heading}</span>
      </div>

      {rows.map(row => (
        <ScoreRowView
          key={row.def.keys[0]}
          row={row}
          peersName={peersName}
          labHref={labHref}
          onLabClick={onLabClick}
        />
      ))}

      {labHref && (
        // The bridge: the card already states two lab-derived scores, this row says the
        // measurements behind them are on this page and names them.
        <div className={styles.bridge}>
          <Linked
            className={styles.bridgeLink}
            href={labHref}
            onClick={e => onLabClick(e, 'hero_bridge')}
          >
            {'See the lab data ↓'}
          </Linked>
          {labSummary && <span className={styles.bridgeMeta}>{labSummary}</span>}
        </div>
      )}
    </section>
  )
}

export default FeaturedScores
